import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

import { setModal } from 'modules/_shared/redux/modalActions';
import CompleteOperationModal from 'modules/trading/modals/CompleteOperationModal';
import { CompleteOperationModalProvider } from 'modules/trading/modals/CompleteOperationModal/context/CompleteOperationModalContext';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import {
  TradingOfferOperation,
  TradingOfferOperationStatus,
} from 'types/TradingOfferOperation';
import { TradingOffer } from 'types/TradingOffer';

import {
  TradingOfferOperationDetailContextType,
  OperationTimelineStep,
  OperationPartyInfo,
  OperationDocumentRole,
} from '../types/TradingOfferOperationDetailContextType';
import { tradingOfferOperationDetailInitValue } from '../constants/TradingOfferOperationDetailInitValue';
import {
  getTradingOfferOperation,
  updateTradingOfferOperation,
  postTradingOfferOperationComment,
  deleteTradingOfferOperationComment,
} from '../../../redux/tradingOfferOperationActions';
import { getTradingOffer } from '../../../redux/tradingOfferActions';
import { timelineStepConfig } from '../../../constants/tradingOfferOperationStatus';
import getOperationReference from '../../../utils/getOperationReference';

const statusOrder: TradingOfferOperationStatus[] = [
  'PENDING',
  'KYC_REVIEW',
  'VALIDATION',
  'PAYMENT_PENDING',
  'TRANSFER_PENDING',
  'COMPLETED',
];

const OPERATION_DETAIL_FIELDS_TO_POPULATE = [
  'buyer',
  'seller',
  'shareClass',
  'tradingOffer',
  'buyerDocuments',
  'sellerDocuments',
  'equityMarketPurchaseContract',
  'comments.user',
] as const;

const getTimelineSteps = (
  currentStatus: TradingOfferOperationStatus
): OperationTimelineStep[] => {
  const statusIndex = statusOrder.indexOf(currentStatus);

  return statusOrder.map((status, index) => ({
    key: status,
    label: status,
    icon: timelineStepConfig[status]?.icon || 'ni-circle',
    description: timelineStepConfig[status]?.description || '',
    isCompleted: index < statusIndex || currentStatus === 'COMPLETED',
    isCurrent: index === statusIndex,
  }));
};

export const TradingOfferOperationDetailContext =
  createContext<TradingOfferOperationDetailContextType>(
    tradingOfferOperationDetailInitValue
  );

export const TradingOfferOperationDetailProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId, operationId } = useParams<{
    societyId: string;
    operationId: string;
  }>();
  const { t } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const partnerQuery = new URLSearchParams(location.search).get('partnerId');
  const partnerQueryParam = partnerQuery ? `?partnerId=${partnerQuery}` : '';

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const [operation, setOperation] = useState<TradingOfferOperation | null>(
    null
  );
  const [tradingOffer, setTradingOffer] = useState<TradingOffer | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const operationStatus = useMemo(
    (): TradingOfferOperationStatus => operation?.status || 'PENDING',
    [operation]
  );

  const operationReference = useMemo(
    () => getOperationReference(operation),
    [operation]
  );

  const timelineSteps = useMemo(
    () => getTimelineSteps(operationStatus),
    [operationStatus]
  );

  const totalShares = actualSociety?.sharesCount?.actual || 0;

  const buyerInfo = useMemo((): OperationPartyInfo | null => {
    if (!operation?.buyer || typeof operation.buyer === 'string') return null;
    const { buyer } = operation;
    const sharesBefore = buyer.sharesCount?.actual || 0;
    const sharesAfter = sharesBefore + (operation.sharesCount || 0);
    return {
      partnerId: buyer._id || '',
      name: buyer.name || '',
      cif: buyer.cif || '',
      email: buyer.email || '',
      partnerNumber: buyer.partnerNumber || 0,
      sharesBefore,
      sharesAfter,
      capitalPercentageAfter:
        totalShares > 0 ? (sharesAfter / totalShares) * 100 : 0,
    };
  }, [operation, totalShares]);

  const sellerInfo = useMemo((): OperationPartyInfo | null => {
    if (!operation?.seller || typeof operation.seller === 'string') return null;
    const { seller } = operation;
    const sharesBefore = seller.sharesCount?.actual || 0;
    const sharesAfter = sharesBefore - (operation.sharesCount || 0);
    const isHidden = tradingOffer?.isSellerNameHidden === true;
    return {
      partnerId: seller._id || '',
      name: isHidden ? t('HiddenName') : seller.name || '',
      cif: isHidden ? '' : seller.cif || '',
      email: isHidden ? t('HiddenEmail') : seller.email || '',
      partnerNumber: seller.partnerNumber || 0,
      sharesBefore,
      sharesAfter,
      capitalPercentageAfter:
        totalShares > 0 ? (sharesAfter / totalShares) * 100 : 0,
    };
  }, [operation, totalShares, tradingOffer?.isSellerNameHidden, t]);

  const userDocumentRole = useMemo((): OperationDocumentRole | null => {
    if (!partnerQuery || !operation) return null;
    const buyerId =
      operation.buyer && typeof operation.buyer !== 'string'
        ? operation.buyer._id
        : '';
    const sellerId =
      operation.seller && typeof operation.seller !== 'string'
        ? operation.seller._id
        : '';
    if (partnerQuery === buyerId) return 'buyer';
    if (partnerQuery === sellerId) return 'seller';
    return null;
  }, [partnerQuery, operation]);

  const canPostComment = useMemo(() => {
    if (isAdmin) return true;
    if (!operation || !user?.partners?.length) return false;
    const buyerId =
      operation.buyer && typeof operation.buyer !== 'string'
        ? operation.buyer._id
        : operation.buyer || '';
    const sellerId =
      operation.seller && typeof operation.seller !== 'string'
        ? operation.seller._id
        : operation.seller || '';
    const pids = user.partners.map((p) => String(p));
    const b = buyerId ? pids.includes(String(buyerId)) : false;
    const s = sellerId ? pids.includes(String(sellerId)) : false;
    return b || s;
  }, [isAdmin, operation, user]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleGoToOffer = useCallback(() => {
    if (!tradingOffer) return;
    history.push(
      `/detalle-oferta/${societyId}/${tradingOffer._id}${partnerQueryParam}`
    );
  }, [history, societyId, tradingOffer, partnerQueryParam]);

  const handleRefreshOperation = useCallback(async () => {
    if (!societyId || !operationId || !actualSociety) return;

    try {
      const operationData = await (dispatch as any)(
        getTradingOfferOperation(operationId, {
          fieldsToPopulate: [...OPERATION_DETAIL_FIELDS_TO_POPULATE],
        })
      );
      if (operationData) {
        setOperation(operationData);
      }
    } catch (error) {
      console.error('Error refreshing operation:', error);
    }
  }, [societyId, operationId, actualSociety, dispatch]);

  const handleUpdateStatus = useCallback(
    async (status: TradingOfferOperationStatus, comment?: string) => {
      if (!operation) return;

      if (status === 'COMPLETED') {
        dispatch(
          setModal(
            <CompleteOperationModalProvider
              operation={operation}
              onSuccess={handleRefreshOperation}
            >
              <CompleteOperationModal />
            </CompleteOperationModalProvider>
          )
        );
        return;
      }

      const result = await (dispatch as any)(
        updateTradingOfferOperation(operation._id, {
          status,
          statusComment: comment,
          updatedBy: user?.['_id'],
        } as any)
      );

      if (result) {
        setOperation(result);
      }
    },
    [operation, dispatch, user, handleRefreshOperation]
  );

  const handleAddComment = useCallback(
    async (text: string) => {
      if (!operation?._id) return;
      const asPartnerId =
        isAdmin && userDocumentRole && partnerQuery ? partnerQuery : undefined;
      const result = await (dispatch as any)(
        postTradingOfferOperationComment(operation._id, {
          text,
          ...(asPartnerId ? { asPartnerId } : {}),
        })
      );
      if (result) {
        setOperation(result);
      }
    },
    [operation, dispatch, isAdmin, userDocumentRole, partnerQuery]
  );

  const handleRemoveComment = useCallback(
    async (commentId: string) => {
      if (!operation?._id) return;
      const result = await (dispatch as any)(
        deleteTradingOfferOperationComment(operation._id, commentId)
      );
      if (result) {
        setOperation(result);
      }
    },
    [operation, dispatch]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!societyId || !operationId) return;

      setIsLoading(true);

      try {
        const operationData = await (dispatch as any)(
          getTradingOfferOperation(operationId, {
            fieldsToPopulate: [...OPERATION_DETAIL_FIELDS_TO_POPULATE],
          })
        );

        if (operationData) {
          setOperation(operationData);

          const offerId =
            typeof operationData.tradingOffer === 'string'
              ? operationData.tradingOffer
              : operationData.tradingOffer?._id;

          if (offerId) {
            const offerData = await (dispatch as any)(getTradingOffer(offerId));
            if (offerData) {
              setTradingOffer(offerData);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching trading offer operation detail:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (actualSociety) {
      fetchData();
    }
  }, [societyId, operationId, actualSociety, dispatch]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        operation,
        tradingOffer,
        operationReference,
        operationStatus,
        timelineSteps,
        buyerInfo,
        sellerInfo,
        isAdmin,
        isLoading: isLoading || !actualSociety,
        partnerId: partnerQuery || '',
        userDocumentRole,
        canPostComment,
      },
      actions: {
        handleGoBack,
        handleUpdateStatus,
        handleGoToOffer,
        handleRefreshOperation,
        handleAddComment,
        handleRemoveComment,
      },
    }),
    [
      societyId,
      actualSociety,
      operation,
      tradingOffer,
      operationReference,
      operationStatus,
      timelineSteps,
      buyerInfo,
      sellerInfo,
      isAdmin,
      isLoading,
      partnerQuery,
      userDocumentRole,
      canPostComment,
      handleGoBack,
      handleUpdateStatus,
      handleGoToOffer,
      handleRefreshOperation,
      handleAddComment,
      handleRemoveComment,
    ]
  );

  return (
    <TradingOfferOperationDetailContext.Provider value={contextValue}>
      {children}
    </TradingOfferOperationDetailContext.Provider>
  );
};

export const useTradingOfferOperationDetail = () => {
  const context = useContext(TradingOfferOperationDetailContext);
  if (!context) {
    throw new Error(
      'useTradingOfferOperationDetail must be used within a TradingOfferOperationDetailProvider'
    );
  }
  return context;
};
